import React from 'react';
import {Position, useInternalNode, getBezierPath, BaseEdge} from '@xyflow/react';

/**
 * Floating edge used for the dimmed system-connection backdrop in data view.
 * It anchors to the node BORDERS (facing each other) rather than a fixed handle,
 * so the connection is always drawn at the edge of the node instead of hiding
 * behind it. Standard React Flow floating-edge geometry (free tier).
 */

const getNodeIntersection = (intersectionNode, targetNode) => {
  const {width: iw, height: ih} = intersectionNode.measured;
  const ipos = intersectionNode.internals.positionAbsolute;
  const tpos = targetNode.internals.positionAbsolute;
  const w = iw / 2;
  const h = ih / 2;
  const x2 = ipos.x + w;
  const y2 = ipos.y + h;
  const x1 = tpos.x + targetNode.measured.width / 2;
  const y1 = tpos.y + targetNode.measured.height / 2;
  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1);
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  return {x: w * (xx3 + yy3) + x2, y: h * (yy3 - xx3) + y2};
};

const getEdgePosition = (node, point) => {
  const n = node.internals.positionAbsolute;
  const nx = Math.round(n.x);
  const ny = Math.round(n.y);
  const px = Math.round(point.x);
  const py = Math.round(point.y);
  if (px <= nx + 1) return Position.Left;
  if (px >= nx + node.measured.width - 1) return Position.Right;
  if (py <= ny + 1) return Position.Top;
  if (py >= ny + node.measured.height - 1) return Position.Bottom;
  return Position.Top;
};

export const getEdgeParams = (source, target) => {
  const sp = getNodeIntersection(source, target);
  const tp = getNodeIntersection(target, source);
  return {
    sx: sp.x, sy: sp.y, tx: tp.x, ty: tp.y,
    sourcePos: getEdgePosition(source, sp),
    targetPos: getEdgePosition(target, tp),
  };
};

const FloatingSystemEdge = ({id, source, target, markerStart, markerEnd, style, data}) => {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  if (!sourceNode || !targetNode) return null;
  const {sx, sy, tx, ty, sourcePos, targetPos} = getEdgeParams(sourceNode, targetNode);
  const [path] = getBezierPath({
    sourceX: sx, sourceY: sy, sourcePosition: sourcePos,
    targetPosition: targetPos, targetX: tx, targetY: ty,
  });
  const autoDerived = data && data.autoDerived;
  return (
    <BaseEdge
      id={id} path={path} markerStart={markerStart} markerEnd={markerEnd}
      style={style}
      className={autoDerived ? 'ln-edge-auto-derived' : undefined}
      title={autoDerived ? 'auto-derived (best-effort SQL parse)' : undefined}
    />
  );
};

export default FloatingSystemEdge;
